import { downloadBrandMappings, updateFranchableName } from '@/api/admin';
import { getAdminFranchisors } from '@/api/consultants';
import { Franchise } from '@/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Dropdown, Flex, message, Typography, Space, } from 'antd';
import { SvgIcon } from 'src/components/icon';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Franchisers from 'src/components/admin/franchisers';
import AddBrandMappingModal from '@/components/admin/brand-mapping-modal';
import DeleteBrandMappingModal from '@/components/admin/brand-mapping-modal/delete';

export default function AdminBrandMappingManagement() {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(null);
  const [isAddOrEdit, setIsAddOrEdit] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deletedFranchableNameRef = useRef('');

  const queryClient = useQueryClient();

  const { data } = useQuery<
    {
      total: number;
      page: number;
      limit: number;
      franchises: Franchise[];
    },
    Error
  >({
    queryKey: ['franchisors', searchParams.toString(), page, limit],
    queryFn: () =>
      getAdminFranchisors({
        search: searchParams.get('search') || '',
        page,
        limit,
      }),
  });

  const { mutateAsync: saveMappingMutate, isPending: isSaving } = useMutation({
    mutationFn: ({
      franchiseId,
      franchableName,
      isDelete = false,
    }: {
      franchiseId: string;
      franchableName: string;
      isDelete?: boolean;
    }) => updateFranchableName(franchiseId, franchableName, isDelete),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['franchisors'] });
      if (variables.isDelete) {
        message.success('The franchable name "' + deletedFranchableNameRef.current + '" has been deleted');
      } else {
        message.success('The franchable name "' + variables.franchableName + '" was added');
      }
    },
    onError: (error: any) => {
      console.error(error);
      message.error(error.message || 'Failed to save brand mapping.');
    },
  });

  
  const handleSaveMapping = async (franchiseId: string, franchableName: string) => {
    try {
      await saveMappingMutate({ franchiseId, franchableName });
      setIsModalOpen(false);
    } catch (error) {
      message.error('Failed to save brand mapping. Please try again.');
    }
  };

  const { mutateAsync: downloadBrandMappingsMutate } = useMutation({
    mutationFn: () =>
      downloadBrandMappings({
        search: searchParams.get('search') || '',
      }),
    onError: (error: any) => {
      console.error(error);
      message.error(
        error.message || 'Brand mappings download failed. Please try again.',
      );
    },
  });

  const handleDownloadTable = async () => {
    try {
      const { file: base64Response } = await downloadBrandMappingsMutate();

      if (!base64Response || typeof base64Response !== 'string') {
        throw new Error('Invalid file data');
      }

      const csvContent = atob(base64Response);
      const blob = new Blob([csvContent], { type: 'text/csv' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `brand_mappings_${new Date().toISOString()}.csv`;
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      message.success('File downloaded successfully');
    } catch (error: any) {
      console.error(error);
      message.error(error.message || 'Failed to download file.');
    }
  };

  const handleModalOpen = (franchise: Franchise | null, type: string) => {
    if (franchise) {
      setSelectedFranchise(franchise);
    }
    setIsAddOrEdit(type);
    setIsModalOpen(true);
  }

  const handleDeleteModalOpen = (franchise: Franchise) => {
    setSelectedFranchise(franchise);
    setIsDeleteModalOpen(true);
  }

  const handleDeleteMapping = async (franchiseId: string, franchableName: string) => {
    deletedFranchableNameRef.current = franchableName;
    try {
      await saveMappingMutate({ franchiseId, franchableName, isDelete: true });
      setIsDeleteModalOpen(false);
    } catch (error) {
      message.error('Failed to delete brand mapping. Please try again.');
    }
  };


  const columns = [
    {
      title: 'Brand Name',
      dataIndex: 'company',
      key: 'company',
      render: (company: string) => company || '-',
    },
    {
      title: 'Franchable Name',
      dataIndex: 'franchableName',
      key: 'franchableName',
      render: (franchableName: string) => franchableName || '-',
    },
        {
      title: 'Action',
      dataIndex: 'actions',
      key: 'actions',
      render: (_: any, record: Franchise) => {
        const menu = {
          items:
            record?.franchableName
              ? [
                  {
                    key: 'edit',
                    label: (
                      <Space>
                        <SvgIcon type="edit" width={16} height={16} style={{  verticalAlign: 'text-top' }} />
                        <span style={{ verticalAlign: 'middle' }}>Edit</span>
                      </Space>
                    ),
                    onClick: () => handleModalOpen(record as any, 'edit'),
                  },
                  {
                    key: 'delete',
                    label: (
                      <Space>
                        <SvgIcon type="deleteRed" width={16} height={16} style={{  verticalAlign: 'text-top' }} />
                        <span style={{ verticalAlign: 'middle', color: '#DD2928' }}>Delete</span>
                      </Space>
                    ),
                    onClick: () => handleDeleteModalOpen(record as any),
                  },
                ]
              : [
                  {
                    key: 'add',
                    label: (
                      <Space>
                        <span style={{ fontWeight: '600', fontSize: '18px' }}>+</span>
                        <span style={{ verticalAlign: 'middle' }}>Add Mapping</span>
                      </Space>
                    ),
                    onClick: () => handleModalOpen(record as any, 'add'),
                  },
                ],
        };

        return (
          <Dropdown menu={menu}>
            <Button type="link">
              <Space>
                <SvgIcon type="threeDots" width={18} height={18} />
              </Space>
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center">
        <Typography.Title level={2} style={{ margin: '0 0 24px', }}> 
          Brand Mapping Management
        </Typography.Title>
        <Button
          type="primary"
          className="admin-button-download-table"
          onClick={() => handleModalOpen(null,'')}
        >
          Add New Mapping
        </Button>
      </Flex>
      <Franchisers
        franchisors={data?.franchises || []}
        columns={columns}
        page={page}
        total={data?.total || 0}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
        downloadData={handleDownloadTable}
        isExpandable={false}
        isMappingPage={true}
      />
      <AddBrandMappingModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMapping}
        saving={isSaving}
        franchise={selectedFranchise}
        type={isAddOrEdit}
      />
      <DeleteBrandMappingModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteMapping}
        franchise={selectedFranchise}
        deleting={isSaving}
      />

    </>
  );
}
